import {useState, useRef, useMemo, memo, useEffect} from 'react';

import styled, {css} from 'styled-components';
import {useSelector} from 'react-redux';

import {selectUserData, selectUserLoading} from '~/shared/store/selectors';
import {loginWithProvider, OAuthProviders} from '~/shared/services/auth';
import {pushRoute, useLocation} from '~/shared/router';
import {SimpleSpinnerLoader} from '~/shared/components/Loaders';
import {createLogger} from '~/shared/logging';
import ManagerProvider from '~/shared/managers/ManagerProvider';
import {trackEvent} from '~/shared/services/analytics';
import {FlexCenter, flexColumn} from '~/shared/theme/FlexLayout';
import {FormNamesEnum, LoginRegistrationProviders} from '~/shared/services/analytics/analyticsModels';
import {media} from '~/shared/theme/media';
import LoginOrRegisterHeader from '~/shared/components/SignIn/LoginOrRegisterHeader';
import {isJustEat} from '~/shared/config';
import {useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';

import {
  convertQueryOrPropToStartingScreenKey,
  getStartingModeFromStartingScreenKey,
  openLoginScreenHandler,
  openProviderScreenHandler,
  openSignUpScreenHandler,
} from './utils/facebookLoginOrSignUpHelper';
import ProviderLoginOrSignUp from './ProviderLoginOrSignUp';
import SignUp from './SignUp';
import Login from './Login';
import {LoginOrRegisterScreenKey, LoginProvider, AuthFlowStep, ViewModes, LOGIN_PROVIDER_ANALYTICS_MAP} from './consts';
import RegisterAddressFinder from './RegisterAddressFinder';
import {handleSuccessAuthNavigation} from './utils/authHelpers';

const logger = createLogger('LoginOrRegister');

const screens = {
  login: {
    key: LoginOrRegisterScreenKey.LOGIN,
    view: Login,
    modeName: ViewModes.SIGN_IN_MODE,
  },
  signup: {
    key: LoginOrRegisterScreenKey.SIGN_UP,
    view: SignUp,
    modeName: ViewModes.SIGN_UP_MODE,
  },
  provider: {
    key: LoginOrRegisterScreenKey.PROVIDER,
    view: ProviderLoginOrSignUp,
  },
  addressFinder: {
    key: LoginOrRegisterScreenKey.ADDRESS_FINDER,
    view: RegisterAddressFinder,
  },
};

const loginBoxShadow = css`
  box-shadow: ${({theme}) => theme.shadows.cardElevation};
`;

const LoginOrRegisterContainer = styled.div`
  position: relative;
  ${flexColumn}
  background-color: ${({theme}) => theme.colors.surface};
  -webkit-overflow-scrolling: touch;
  width: 100%;
  max-width: 498px;
  margin: 0 auto;
  ${({pageView}) => !pageView && `
    overflow: auto;
    &&& {
      width: 505px;
    }
  `}
  ${({pageView}) => pageView && css`
    ${media.minLargeMobile`
      height: fit-content;
      border-radius: 16px;
      ${({removeShadow}) => !removeShadow && loginBoxShadow}
    `}
  `}
`;

const LoaderOverlay = styled.div`
  position: absolute;
  background-color: ${({theme}) => theme.colors.surface};
  z-index: ${({theme}) => theme.zIndex.menuSubHeader};
  inset: 0;
  border-radius: 16px;
  min-height: 508px;
  ${media.minLargeMobile`
    ${loginBoxShadow}
  `}
`;

const LoaderContainer = styled(FlexCenter)`
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 508px;
`;

const LoginOrRegister = props => {
  const {currentScreenKey, authFlowStep} = props;
  const screen = authFlowStep === AuthFlowStep.ADD_ADDRESS ? screens.addressFinder : screens[currentScreenKey];
  const {view: Component, mode} = screen;

  return <Component {...props} mode={mode} />;
};

const LoginOrRegisterWrapper = ({pageView, mode, onSuccess, backButtonUrl, closeModal, isStrongLogin, fromCheckout}) => {
  const isUserLoading = useSelector(selectUserLoading);
  const userData = useSelector(selectUserData);
  const isMinLargeMobile = useIsMinLargeMobile();
  const location = useLocation();
  const [currentScreenKey, setCurrentScreenKey] = useState(convertQueryOrPropToStartingScreenKey(location.routeParams.mode || mode) || (isJustEat ? screens.signup.key : screens.login.key));
  const providerDetails = useRef();
  const [loginProvider, setLoginProvider] = useState(LoginProvider.TENBIS);
  const [providerViewMode, setProviderViewMode] = useState(getStartingModeFromStartingScreenKey(currentScreenKey));
  const [authFlowStep, setAuthFlowStep] = useState(AuthFlowStep.INITIAL);
  const [verificationDetails, setVerificationDetails] = useState();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialLoginEmail, setInitialLoginEmail] = useState();

  const isRedirectedToStepRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const isLoading = useMemo(() => isUserLoading || isSubmitting || loading, [isUserLoading, isSubmitting, loading]);

  const openLoginScreen = ({email, getAuthCodeForProviderConnect, nextProvider, providerProps} = {}) => openLoginScreenHandler({
    email,
    getAuthCodeForProviderConnect,
    loginProvider: nextProvider || loginProvider,
    setVerificationDetails,
    setLoginProvider,
    setInitialLoginEmail,
    setCurrentScreenKey,
    setProviderViewMode,
    setAuthFlowStep,
    providerProps,
    providerDetails,
  });

  const openSignUpScreen = () => openSignUpScreenHandler({
    loginProvider,
    setCurrentScreenKey,
    setProviderViewMode,
    setLoginProvider,
  });

  const openProviderScreen = ({providerProps, nextLoginProvider, nextScreen}) => openProviderScreenHandler({
    setLoginProvider,
    setCurrentScreenKey,
    providerProps,
    providerDetails,
    nextLoginProvider,
    setProviderViewMode,
    nextScreen,
  });

  const onSubmit = async (values, linkType, providerFromArgs) => {
    try {
      setIsSubmitting(true);
      const userDetails = await ManagerProvider.login(values, {onCheckout: fromCheckout, authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[providerFromArgs || loginProvider]});
      if (onSuccess) {
        onSuccess(userDetails);
      }
      handleSuccessAuthNavigation({pageView, closeModal, fromCheckout});
    } catch (error) {
      const errorType = 'login failed';
      logger.error(errorType, {error, fingerprint: errorType});
      if (linkType) {
        trackEvent('hasFormError', {
          formType: '2FA Login Form',
          formErrorType: error?.message,
          linkType,
        });
      }

      trackEvent('formErrorMessageView', {
        formName: linkType === '2FA - SMS' ? FormNamesEnum.OTP_LOGIN : FormNamesEnum.PASSWORD_LOGIN,
        errorType: error?.message,
        onCheckout: fromCheckout,
        authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[providerFromArgs || loginProvider],
      });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (currentScreenKey === LoginOrRegisterScreenKey.LOGIN) {
      trackEvent('loginForm_view', {onCheckout: fromCheckout});
      return;
    }
    if (currentScreenKey === LoginOrRegisterScreenKey.SIGN_UP) {
      trackEvent('hasViewedRegistrationPopup', {onCheckout: fromCheckout});
    }
  }, [currentScreenKey, fromCheckout]);
  
  const onBackButtonClick = () => {
    if (pageView) {
      return pushRoute(backButtonUrl, {keepAllQueries: false});
    }
    closeModal();
  };

  const openFacebook = async () => {
    if (currentScreenKey === screens.signup.key) {
      trackEvent('hasClickedFacebookLogin_Signup');
      trackEvent('registrationForm_click', {onCheckout: fromCheckout, authenticationProvider: LoginRegistrationProviders.FACEBOOK});
    } else if (currentScreenKey === screens.login.key) {
      trackEvent('hasClickedFacebookLogin_Login');
      trackEvent('loginForm_click', {onCheckout: fromCheckout, authenticationProvider: LoginRegistrationProviders.FACEBOOK});
    }

    const {shouldConnectAccount, providerUserId: facebookUserId, providerAccessToken: facebookUserAccessToken, encryptedId, email} = await loginWithProvider(OAuthProviders.FACEBOOK);

    if (shouldConnectAccount) {
      openProviderScreen({
        providerProps: {
          facebookUserId,
          facebookUserAccessToken,
          shouldConnectAccount: true,
          email,
        },
        nextLoginProvider: LoginProvider.FACEBOOK,
      });

      return;
    }

    onSubmit({facebookUserId, facebookUserAccessToken, userToken: encryptedId}, null, LoginProvider.FACEBOOK);
  };

  const onSubmitWithGoogleConnect = () => {
    onSubmit({}, null, LoginProvider.GOOGLE);
  };

  useEffect(() => {
    if (!isUserLoading && !!userData && !isStrongLogin && authFlowStep === AuthFlowStep.INITIAL) {
      handleSuccessAuthNavigation({pageView, closeModal});
    }
  }, [authFlowStep, isUserLoading, userData, isStrongLogin, pageView, closeModal]);

  return (
    <LoginOrRegisterContainer pageView={pageView} removeShadow={isLoading}>
      <LoginOrRegisterHeader
        viewMode={providerViewMode}
        authFlowStep={authFlowStep}
        setAuthFlowStep={setAuthFlowStep}
        onSignInClick={() => {
          trackEvent('hasClickedIHaveAnAccount');
          openLoginScreen();
        }}
        onSignUpClick={() => {
          trackEvent('hasClickedCreateAccount');
          openSignUpScreen();
        }}
        hideHeader={isStrongLogin || authFlowStep === AuthFlowStep.RESET_PASSWORD_SENT}
        onBackButtonClick={onBackButtonClick}
        isMinLargeMobile={isMinLargeMobile}
      />
      <LoginOrRegister
        {...{
          loginProvider,
          authFlowStep,
          setAuthFlowStep,
          providerViewMode,
          isSubmitting,
          setIsSubmitting,
          initialLoginEmail,
          loading: isLoading,
          currentScreenKey,
          openLoginScreen,
          pageView,
          openSignUpScreen,
          onSubmit,
          onSuccess,
          onBackButtonClick,
          openFacebook,
          isStrongLogin,
          openProviderScreen,
          onSubmitWithGoogleConnect,
          ...(providerDetails.current || {}),
          isRedirectedToStepRef,
          verificationDetails,
          setVerificationDetails,
          fromCheckout,
          closeModal,
          setLoading,
          setInitialLoginEmail,
        }}
      />
      {isLoading && (
        <LoaderOverlay>
          <LoaderContainer>
            <SimpleSpinnerLoader variant="pieBig" />
          </LoaderContainer>
        </LoaderOverlay>
      )}
    </LoginOrRegisterContainer>
  );
};

export default memo(LoginOrRegisterWrapper);
